import { motion } from "framer-motion";
import { Building2, Phone, CheckCircle2 } from "lucide-react";
import { formatPhoneOnInput, validateGhanaPhoneNumber } from "../../../utils/formValidators";
import { useState } from "react";

export default function Step1CompanyInfo({ formData, setFormData, errors }) {
  const [phoneValidation, setPhoneValidation] = useState({
    telephoneNumber: null,
    cellNumber: null,
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePhoneChange = (field, value) => {
    const formatted = formatPhoneOnInput(value);
    handleChange(field, formatted);
    
    const validation = validateGhanaPhoneNumber(formatted.replace(/\s/g, ""));
    setPhoneValidation((prev) => ({
      ...prev,
      [field]: validation.isValid ? "valid" : null,
    }));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="p-8 bg-white rounded-xl border border-gray-200 shadow-sm"
    >
      {/* Section Title */}
      <div className="flex gap-3 items-center mb-8">
        <div className="flex justify-center items-center w-10 h-10 text-white bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-md">
          <Building2 size={20} />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Company Information</h2>
      </div>

      {/* Form Fields - Two Columns */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Name of Company */}
          <div>
            <label className="block mb-2 text-sm font-semibold text-gray-700">
              Name of Company <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.companyName || ""}
              onChange={(e) => handleChange("companyName", e.target.value)}
              className={`w-full px-4 py-3 text-gray-900 bg-white rounded-lg border transition-all outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                errors?.companyName
                  ? "border-red-300 bg-red-50"
                  : "border-gray-300 hover:border-gray-400"
              }`}
              placeholder="Enter company name"
            />
            {errors?.companyName && (
              <p className="mt-1 text-sm text-red-500">{errors.companyName}</p>
            )}
          </div>

          {/* Telephone Number */}
          <div>
            <label className="flex gap-2 items-center mb-2 text-sm font-semibold text-gray-700">
              <Phone size={16} className="text-orange-600" />
              Telephone Number <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="tel"
                value={formData.telephoneNumber || ""}
                onChange={(e) => handlePhoneChange("telephoneNumber", e.target.value)}
                className={`w-full px-4 py-3 pr-10 text-gray-900 bg-white rounded-lg border transition-all outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  errors?.telephoneNumber
                    ? "border-red-300 bg-red-50"
                    : phoneValidation.telephoneNumber === "valid"
                    ? "border-green-300 bg-green-50"
                    : "border-gray-300 hover:border-gray-400"
                }`}
                placeholder="0241234567 or 233241234567"
              />
              {phoneValidation.telephoneNumber === "valid" && (
                <CheckCircle2 className="absolute top-3.5 right-3 text-green-500" size={20} />
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Format: 233XXXXXXXXX (12 digits) or 0XXXXXXXXX (10 digits)
            </p>
            {errors?.telephoneNumber && (
              <p className="mt-1 text-sm text-red-500">
                {errors.telephoneNumber}
              </p>
            )}
          </div>

          {/* Fax Number */}
          <div>
            <label className="block mb-2 text-sm font-semibold text-gray-700">
              Fax Number
            </label>
            <input
              type="tel"
              value={formData.faxNumber || ""}
              onChange={(e) => handleChange("faxNumber", e.target.value)}
              className="px-4 py-3 w-full text-gray-900 bg-white rounded-lg border border-gray-300 transition-all outline-none hover:border-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="Enter fax number (optional)"
            />
          </div>

          {/* Website Address */}
          <div>
            <label className="block mb-2 text-sm font-semibold text-gray-700">
              Website Address
            </label>
            <input
              type="url"
              value={formData.websiteAddress || ""}
              onChange={(e) => handleChange("websiteAddress", e.target.value)}
              className="px-4 py-3 w-full text-gray-900 bg-white rounded-lg border border-gray-300 transition-all outline-none hover:border-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="https://www.example.com"
            />
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Company VAT Registration Number */}
          <div>
            <label className="block mb-2 text-sm font-semibold text-gray-700">
              Company VAT Registration Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.vatRegistrationNumber || ""}
              onChange={(e) =>
                handleChange("vatRegistrationNumber", e.target.value)
              }
              className={`w-full px-4 py-3 text-gray-900 bg-white rounded-lg border transition-all outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                errors?.vatRegistrationNumber
                  ? "border-red-300 bg-red-50"
                  : "border-gray-300 hover:border-gray-400"
              }`}
              placeholder="Enter VAT registration number"
            />
            {errors?.vatRegistrationNumber && (
              <p className="mt-1 text-sm text-red-500">
                {errors.vatRegistrationNumber}
              </p>
            )}
          </div>

          {/* Cell Number */}
          <div>
            <label className="flex gap-2 items-center mb-2 text-sm font-semibold text-gray-700">
              <Phone size={16} className="text-orange-600" />
              Cell Number <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="tel"
                value={formData.cellNumber || ""}
                onChange={(e) => handlePhoneChange("cellNumber", e.target.value)}
                className={`w-full px-4 py-3 pr-10 text-gray-900 bg-white rounded-lg border transition-all outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  errors?.cellNumber
                    ? "border-red-300 bg-red-50"
                    : phoneValidation.cellNumber === "valid"
                    ? "border-green-300 bg-green-50"
                    : "border-gray-300 hover:border-gray-400"
                }`}
                placeholder="0241234567 or 233241234567"
              />
              {phoneValidation.cellNumber === "valid" && (
                <CheckCircle2 className="absolute top-3.5 right-3 text-green-500" size={20} />
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Format: 233XXXXXXXXX (12 digits) or 0XXXXXXXXX (10 digits)
            </p>
            {errors?.cellNumber && (
              <p className="mt-1 text-sm text-red-500">{errors.cellNumber}</p>
            )}
          </div>

          {/* Business Email Address */}
          <div>
            <label className="block mb-2 text-sm font-semibold text-gray-700">
              Business Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.businessEmail || ""}
              onChange={(e) => handleChange("businessEmail", e.target.value)}
              className={`w-full px-4 py-3 text-gray-900 bg-white rounded-lg border transition-all outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                errors?.businessEmail
                  ? "border-red-300 bg-red-50"
                  : "border-gray-300 hover:border-gray-400"
              }`}
              placeholder="business@example.com"
            />
            {errors?.businessEmail && (
              <p className="mt-1 text-sm text-red-500">{errors.businessEmail}</p>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

